/**
 * 🔐 Role Permissions Management Service
 * Handles fetching and updating role permissions
 */

import apiClient from './apiClient';

export interface RolePermissions {
  role: string;
  permissions: string[];
  description?: string;
  isSystemRole: boolean;
  isEditable: boolean;
}

export interface PermissionCategory {
  [category: string]: string[];
}

export interface RolesResponse {
  success: boolean;
  roles?: RolePermissions[];
  allPermissions?: string[];
  permissionCategories?: PermissionCategory;
  error?: string;
}

export interface UpdatePermissionsResponse {
  success: boolean;
  message?: string;
  error?: string;
}

export const rolePermissionsService = {
  /**
   * Fetch all roles with their permissions
   * GET /api/v1/admin/roles
   */
  async getRoles(): Promise<RolesResponse> {
    try {

      const response = await apiClient.get('/api/v1/admin/roles');


      if (response.data.success) {
        return {
          success: true,
          roles: response.data.roles || [],
          allPermissions: response.data.allPermissions || [],
          permissionCategories: response.data.permissionCategories || {}
        };
      } else {
        return {
          success: false,
          error: response.data.error || 'Failed to fetch roles'
        };
      }
    } catch (error: any) {
      console.error('❌ Error fetching roles:', error);
      return {
        success: false,
        error: error.response?.data?.error || error.message || 'Failed to fetch roles'
      };
    }
  },

  /**
   * Update permissions for a specific role
   * PUT /api/v1/admin/roles/[role]/permissions
   */
  async updateRolePermissions(
    role: string,
    permissions: string[]
  ): Promise<UpdatePermissionsResponse> {
    try {

      const response = await apiClient.put(
        `/api/v1/admin/roles/${role}/permissions`,
        { permissions }
      );


      if (response.data.success) {
        return {
          success: true,
          message: response.data.message || `Permissions updated for ${role}`
        };
      } else {
        return {
          success: false,
          error: response.data.error || 'Failed to update permissions'
        };
      }
    } catch (error: any) {
      console.error('❌ Error updating permissions:', error);
      return {
        success: false,
        error: error.response?.data?.error || error.message || 'Failed to update permissions'
      };
    }
  },

  /**
   * Check if a role has a specific permission
   */
  hasPermission(rolePermissions: string[], permission: string): boolean {
    // Super admin wildcard
    if (rolePermissions.includes('*')) {
      return true;
    }

    return rolePermissions.includes(permission);
  },

  /**
   * Get permissions organized by category
   */
  organizePermissionsByCategory(): PermissionCategory {
    return {
      'Booking Management': [
        'bookings:view',
        'bookings:modify',
        'bookings:create',
        'bookings:cancel'
      ],
      'User Management': [
        'users:view',
        'users:create',
        'users:modify',
        'users:suspend'
      ],
      'Verification': [
        'verification:view',
        'verification:approve',
        'verification:reject'
      ],
      'Vehicle Management': [
        'vehicles:view',
        'vehicles:modify',
        'vehicles:approve',
        'vehicles:reject'
      ],
      'Driver Management': [
        'drivers:view',
        'drivers:modify',
        'drivers:approve',
        'drivers:reject'
      ],
      'Finance': [
        'finance:view',
        'finance:modify',
        'finance:reports'
      ],
      'Content Management': [
        'content:legal-documents',
        'content:faq',
        'content:terms',
        'content:privacy'
      ],
      'Reports': [
        'reports:view',
        'reports:export'
      ],
      'Staff Management': [
        'staff:view',
        'staff:create',
        'staff:modify',
        'staff:delete'
      ],
      'System': [
        'audit-logs:view',
        'support:tickets',
        'settings:view'
      ]
    };
  }
};

export default rolePermissionsService;
